import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useIslandSocket } from "@/lib/islandSocket";
import { toast } from "sonner";

const Ctx = createContext({ unread: 0, incoming: null, sendEvent: () => {}, refreshUnread: () => {} });

// One realtime socket for the whole shell: nav unread badge, message alerts, browser notifications.
export function MessagesProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [incoming, setIncoming] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const locRef = useRef(location.pathname); locRef.current = location.pathname;
  const refreshUnread = useCallback(() => api.get("/island/unread").then((r) => setUnread(r.data.unread)).catch(() => {}), []);
  useEffect(() => { refreshUnread(); }, [refreshUnread]);

  const sendEvent = useIslandSocket((ev) => {
    setIncoming({ ...ev, _t: Date.now() });
    if (ev.type === "message" && ev.message?.sender_id !== user?.id) {
      refreshUnread();
      const m = ev.message; const body = m.content?.slice(0, 120) || (m.audio_path ? "🎤 Voice note" : m.gif_url ? "GIF" : "📷 Photo");
      const onChat = locRef.current === "/messages" || locRef.current === "/island";
      if (document.hidden && "Notification" in window && Notification.permission === "granted") {
        try { const n = new Notification(m.sender_name, { body, tag: ev.conversation_id, icon: "/favicon.ico" }); n.onclick = () => { window.focus(); navigate(`/messages?c=${ev.conversation_id}`); }; } catch (e) { console.debug("Desktop notification failed", e); }
      } else if (!onChat) {
        toast(`${m.sender_name}: ${body}`, { description: "New message", action: { label: "View", onClick: () => navigate(`/messages?c=${ev.conversation_id}`) } });
      }
    } else if (ev.type === "mention") {
      refreshUnread();
      toast(`${ev.from} mentioned ${ev.everyone ? "everyone" : "you"} in ${ev.conversation_name}`, { description: ev.preview, icon: "@", action: { label: "View", onClick: () => navigate(`/messages?c=${ev.conversation_id}`) } });
    } else if (ev.type === "read" && ev.user_id === user?.id) refreshUnread();
    else if (ev.type === "meeting" && ev.update?.attendance_open) toast(`📣 Attendance called for "${ev.title}" — tap I'm present!`);
    else if (ev.type === "call") toast(`📞 ${ev.from} started a ${ev.video ? "video" : "voice"} call`);
  });

  return <Ctx.Provider value={{ unread, incoming, sendEvent, refreshUnread }}>{children}</Ctx.Provider>;
}

export const useMessages = () => useContext(Ctx);
